import React, { useState, useRef } from 'react';
import { devLog } from '../utils/devTools';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Grid,
  IconButton,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Delete as DeleteIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';
import { StepProps, Deponent } from '../types';
import FileUploadField from './FileUploadField';

type TimeField = 'startTimeHour' | 'startTimeMinute' | 'startTimeAmPm' | 'endTimeHour' | 'endTimeMinute' | 'endTimeAmPm';

interface DeponentFile {
  name?: string;
  originalName?: string;
  filename?: string;
  size?: number;
  type?: string;
  mimetype?: string;
  thumbnailUrl?: string;
  url?: string;
  deponentId?: string;
}

const DeponentInfo: React.FC<StepProps> = ({ onNext }) => {
  const {
    formData,
    updateNestedFormData,
    updateMultipleNestedFields,
    addItemToArray,
    removeItemFromArray,
    validationErrors,
    validateStep
  } = useFormContext();
  
  // Generate a temporary ID for tracking uploads for the new deponent
  const tempId = useRef(`deponent-${Date.now()}-${formData.deponents.length}`);
  
  // State to track which accordion panel is expanded (default to first one or empty string if no deponents)
  // Use useRef to remember the initial state but not trigger re-renders
  const initialExpanded = useRef(formData.deponents.length > 0 ? formData.deponents[0].id : '');
  const [expandedPanel, setExpandedPanel] = useState<string>(initialExpanded.current);

  // Memoize the handler to prevent recreating on each render
  const handleAccordionChange = React.useCallback((panel: string) => 
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : '');
    }, []);
  
  const [newDeponent, setNewDeponent] = useState<Deponent>({
    name: '',
    exhibitStatus: '',
    exhibitCarrier: '',
    exhibitTrackingNumber: '',
    readAndSign: undefined,
    sendErrataTo: '',
    specialProductionNotes: '',
    id: tempId.current, // Use the temporary ID
    numPages: 0,
    startTimeHour: '',
    startTimeMinute: '',
    startTimeAmPm: '',
    endTimeHour: '',
    endTimeMinute: '',
    endTimeAmPm: '',
    transcriptType: 'standard',
    transcriptFiles: [],
    exhibitFiles: [],
    audioFiles: [],
    techExpert: false,
    video: false,
    interpreter: false,
    afterHours: false,
    weekendHoliday: false
  });

  // Memoize handlers to prevent recreating on each render
  // Convert time fields to 24-hour format
  const convertToTimeString = React.useCallback((hour: string, minute: string, ampm: string): string | undefined => {
    if (!hour || !minute || !ampm) return undefined;

    let hourNum = parseInt(hour, 10);

    // Convert from 12-hour to 24-hour format
    if (ampm === 'PM' && hourNum !== 12) hourNum += 12;
    if (ampm === 'AM' && hourNum === 12) hourNum = 0;

    // Format to HH:MM
    return `${hourNum.toString().padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }, []);
  
  // Update a time UI field and atomically derive the 24-hour format in a single state update.
  // Uses a callback with updateMultipleNestedFields to read the latest state for the other
  // time fields, avoiding stale closure issues with React's batched updates.
  const updateTimeField = React.useCallback((
    index: number,
    timeField: 'startTimeHour' | 'startTimeMinute' | 'startTimeAmPm' | 'endTimeHour' | 'endTimeMinute' | 'endTimeAmPm',
    value: string
  ) => {
    updateMultipleNestedFields('deponents', index, (currentDeponent) => {
      const updated: Partial<Deponent> = { [timeField]: value };
      const merged = { ...currentDeponent, ...updated };

      if (timeField.startsWith('startTime')) {
        const startTime = convertToTimeString(merged.startTimeHour, merged.startTimeMinute, merged.startTimeAmPm);
        if (startTime) {
          updated.startTime = startTime;
        }
      } else {
        const endTime = convertToTimeString(merged.endTimeHour, merged.endTimeMinute, merged.endTimeAmPm);
        if (endTime) {
          updated.endTime = endTime;
        }
      }

      return updated;
    });
  }, [updateMultipleNestedFields, convertToTimeString]);
  
  const handleChange = React.useCallback((index: number, field: keyof Deponent, value: string | boolean) => {
    // Special handling for time fields to keep the 24-hour format in sync
    if (['startTimeHour', 'startTimeMinute', 'startTimeAmPm', 'endTimeHour', 'endTimeMinute', 'endTimeAmPm'].includes(field)) {
      updateTimeField(index, field as TimeField, value as string);
    } else {
      // For all other fields, just update normally
      updateNestedFormData('deponents', index, field, value);
    }
  }, [updateNestedFormData, updateTimeField]);
  
  // Log the current deponent data for debugging
  React.useEffect(() => {
    devLog("DeponentInfo - Current deponents:", formData.deponents.map(d => ({
      id: d.id,
      name: d.name,
      transcriptFiles: d.transcriptFiles ? `${d.transcriptFiles.length} files` : 'none',
      audioFiles: d.audioFiles ? `${d.audioFiles.length} files` : 'none',
      exhibitFiles: d.exhibitFiles ? `${d.exhibitFiles.length} files` : 'none'
    })));
  }, [formData.deponents]);

  // Function to add the current working deponent
  const addDeponent = React.useCallback(() => {
    if (newDeponent.name.trim()) {
      devLog("addDeponent called with newDeponent:", {
        id: newDeponent.id,
        name: newDeponent.name,
        transcriptFiles: newDeponent.transcriptFiles ? 
          (Array.isArray(newDeponent.transcriptFiles) ? 
            `${newDeponent.transcriptFiles.length} files` : newDeponent.transcriptFiles) : 'none',
        exhibitFiles: newDeponent.exhibitFiles ? 
          (Array.isArray(newDeponent.exhibitFiles) ? 
            `${newDeponent.exhibitFiles.length} files` : newDeponent.exhibitFiles) : 'none',
        audioFiles: newDeponent.audioFiles ? 
          (Array.isArray(newDeponent.audioFiles) ? 
            `${newDeponent.audioFiles.length} files` : newDeponent.audioFiles) : 'none'
      });
      
      // Save the current deponent ID
      const currentDeponentId = newDeponent.id;

      // Generate a new temporary ID for the next deponent now
      // This ensures that the deponent index will be correct, as adding the current deponent
      // to the list is only eventually consistent
      const newTempId = `deponent-${Date.now()}-${formData.deponents.length+1}`;
      
      // Get any cached files we have for this deponent
      const cachedFiles = uploadedFiles.current[currentDeponentId] || {
        transcriptFiles: [],
        exhibitFiles: [],
        audioFiles: []
      };
      
      // Use newest files available - either from current deponent or cached
      const transcriptFiles = (Array.isArray(newDeponent.transcriptFiles) && newDeponent.transcriptFiles.length > 0) ? 
                               newDeponent.transcriptFiles : cachedFiles.transcriptFiles;
      
      const exhibitFiles = (Array.isArray(newDeponent.exhibitFiles) && newDeponent.exhibitFiles.length > 0) ? 
                             newDeponent.exhibitFiles : cachedFiles.exhibitFiles;
      
      const audioFiles = (Array.isArray(newDeponent.audioFiles) && newDeponent.audioFiles.length > 0) ? 
                           newDeponent.audioFiles : cachedFiles.audioFiles;
      
      devLog("Adding deponent with files:", {
        id: currentDeponentId,
        name: newDeponent.name,
        transcriptFiles: transcriptFiles.length > 0 ? `${transcriptFiles.length} files` : 'none',
        exhibitFiles: exhibitFiles.length > 0 ? `${exhibitFiles.length} files` : 'none',
        audioFiles: audioFiles.length > 0 ? `${audioFiles.length} files` : 'none'
      });
      
      // Create the deponent object with the files and ensure 24-hour time formats are set
      const deponentToAdd = {
        ...newDeponent,
        startTime: convertToTimeString(newDeponent.startTimeHour, newDeponent.startTimeMinute, newDeponent.startTimeAmPm),
        endTime: convertToTimeString(newDeponent.endTimeHour, newDeponent.endTimeMinute, newDeponent.endTimeAmPm),
        transcriptFiles,
        exhibitFiles,
        audioFiles
      };
      
      // Log what files we're actually adding
      devLog("Final files being added to deponent:", {
        transcriptFiles: transcriptFiles.length > 0 ?
          transcriptFiles.map((f: DeponentFile) => f.name || f.originalName || f.filename || 'Unknown file') : 'none',
        exhibitFiles: exhibitFiles.length > 0 ?
          exhibitFiles.map((f: DeponentFile) => f.name || f.originalName || f.filename || 'Unknown file') : 'none',
        audioFiles: audioFiles.length > 0 ?
          audioFiles.map((f: DeponentFile) => f.name || f.originalName || f.filename || 'Unknown file') : 'none'
      });
      
      // Add the deponent with the current ID and files
      addItemToArray('deponents', deponentToAdd);
      
      // Reset the form using for the next deponent
      tempId.current = newTempId;
      setNewDeponent({
        name: '',
        exhibitStatus: '',
        exhibitCarrier: '',
        exhibitTrackingNumber: '',
        readAndSign: undefined,
    sendErrataTo: '',
        id: newTempId, // Use the new ID for the next deponent
        numPages: 0,
        startTimeHour: '',
        startTimeMinute: '',
        startTimeAmPm: '',
        endTimeHour: '',
        endTimeMinute: '',
        endTimeAmPm: '',
        transcriptType: 'standard',
        transcriptFiles: [],
        exhibitFiles: [],
        audioFiles: [],
        techExpert: false,
        video: false,
        interpreter: false,
        afterHours: false,
        weekendHoliday: false
      });
    }
  }, [newDeponent, addItemToArray, formData, convertToTimeString]);

  const handleNewDeponentChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewDeponent(prev => {
      const updatedDeponent = {
        ...prev,
        [name]: value
      };
      
      // Also update the 24-hour time format if needed
      if (['startTimeHour', 'startTimeMinute', 'startTimeAmPm'].includes(name)) {
        const hour = name === 'startTimeHour' ? value : prev.startTimeHour;
        const minute = name === 'startTimeMinute' ? value : prev.startTimeMinute;
        const ampm = name === 'startTimeAmPm' ? value as 'AM' | 'PM' : prev.startTimeAmPm;
        
        if (hour && minute && ampm) {
          updatedDeponent.startTime = convertToTimeString(hour, minute, ampm);
        }
      } else if (['endTimeHour', 'endTimeMinute', 'endTimeAmPm'].includes(name)) {
        const hour = name === 'endTimeHour' ? value : prev.endTimeHour;
        const minute = name === 'endTimeMinute' ? value : prev.endTimeMinute;
        const ampm = name === 'endTimeAmPm' ? value as 'AM' | 'PM' : prev.endTimeAmPm;
        
        if (hour && minute && ampm) {
          updatedDeponent.endTime = convertToTimeString(hour, minute, ampm);
        }
      }
      
      return updatedDeponent;
    });
  }, [convertToTimeString]);

  const handleNewDeponentSelectChange = React.useCallback((e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setNewDeponent(prev => {
      // For select fields like exhibitStatus, etc.
      return {
        ...prev,
        [name as string]: value
      };
    });
    
    // No automatic adding from select changes
  }, []);

  const removeDeponent = React.useCallback((index: number) => {
    if (window.confirm('Are you sure you want to remove this deponent?')) {
      const deponentId = formData.deponents[index].id;
      
      // If we're removing the currently expanded panel, expand the first remaining one
      if (expandedPanel === deponentId) {
        // Find the next deponent to expand, if any
        const remainingDeponents = formData.deponents.filter((_, i) => i !== index);
        if (remainingDeponents.length > 0) {
          setExpandedPanel(remainingDeponents[0].id);
        } else {
          setExpandedPanel('');
        }
      }
      
      removeItemFromArray('deponents', index);
    }
  }, [formData.deponents, expandedPanel, removeItemFromArray]);

  // Create a shared ref for file data outside the component lifecycle
  const uploadedFiles = React.useRef<{
    [deponentId: string]: {
      transcriptFiles: DeponentFile[];
      exhibitFiles: DeponentFile[];
      audioFiles: DeponentFile[];
    }
  }>({});

  // New useEffect to listen for file upload events globally
  React.useEffect(() => {
    const handleFileUpload = (event: CustomEvent) => {
      if (event.detail && event.detail.deponentId && event.detail.files && event.detail.type) {
        const { deponentId, files, type } = event.detail;
        
        // Initialize this deponent's files object if it doesn't exist
        if (!uploadedFiles.current[deponentId]) {
          uploadedFiles.current[deponentId] = {
            transcriptFiles: [],
            exhibitFiles: [],
            audioFiles: []
          };
        }

        // Store the files in our ref
        if (type === 'transcriptFiles' || type === 'exhibitFiles' || type === 'audioFiles') {
          // Use a type-safe approach to set the correct property
          if (type === 'transcriptFiles') {
            uploadedFiles.current[deponentId].transcriptFiles = files;
          } else if (type === 'exhibitFiles') {
            uploadedFiles.current[deponentId].exhibitFiles = files;
          } else if (type === 'audioFiles') {
            uploadedFiles.current[deponentId].audioFiles = files;
          }
          devLog(`DeponentInfo received ${files.length} ${type} for deponent ${deponentId}`);
        }
      }
    };

    // Add global event listener for file uploads
    document.addEventListener('file-upload-complete', handleFileUpload as EventListener);
    
    return () => {
      document.removeEventListener('file-upload-complete', handleFileUpload as EventListener);
    };
  }, []);
  
  // Better approach - dispatch an event to gather all files just before adding the deponent
  const gatherFilesForDeponent = (deponentId: string): Promise<{
    transcriptFiles: DeponentFile[];
    exhibitFiles: DeponentFile[];
    audioFiles: DeponentFile[];
  }> => {
    return new Promise((resolve) => {
      // Create a request event to get all files
      const requestEvent = new CustomEvent('request-files', { 
        detail: { deponentId } 
      });
      
      // Set a timeout to resolve after a short delay if no one responds
      const timeoutId = setTimeout(() => {
        devLog(`No file components responded for deponent ${deponentId}, using stored files`);
        
        // Use any files we have stored in our ref
        resolve(uploadedFiles.current[deponentId] || {
          transcriptFiles: [],
          exhibitFiles: [],
          audioFiles: []
        });
      }, 100);
      
      // Set up a one-time listener for responses
      const handleResponse = (event: Event) => {
        const responseEvent = event as CustomEvent;
        if (responseEvent.detail && responseEvent.detail.deponentId === deponentId) {
          clearTimeout(timeoutId);
          document.removeEventListener('files-response', handleResponse as EventListener);
          
          const { transcriptFiles, exhibitFiles, audioFiles } = responseEvent.detail;
          devLog(`Received files response for deponent ${deponentId}:`);
          devLog(`- ${transcriptFiles?.length || 0} transcript files`);
          devLog(`- ${exhibitFiles?.length || 0} exhibit files`);
          devLog(`- ${audioFiles?.length || 0} audio files`);
          
          // Store these files in our ref for future use
          uploadedFiles.current[deponentId] = {
            transcriptFiles: transcriptFiles || [],
            exhibitFiles: exhibitFiles || [],
            audioFiles: audioFiles || []
          };
          
          resolve({
            transcriptFiles: transcriptFiles || [],
            exhibitFiles: exhibitFiles || [],
            audioFiles: audioFiles || []
          });
        }
      };
      
      document.addEventListener('files-response', handleResponse as EventListener);
      
      // Dispatch the request
      document.dispatchEvent(requestEvent);
    });
  };

  const handleNext = React.useCallback(() => {
    // If there's a partially filled deponent in the form, add it before proceeding
    if (newDeponent.name.trim()) {
      devLog("Handling next with partially filled deponent form - adding deponent", newDeponent.name);
      const deponentId = newDeponent.id;
      
      // Get all files associated with this deponent
      gatherFilesForDeponent(deponentId).then(files => {
        devLog(`Gathered files for deponent ${deponentId}:`, files);
        
        const deponentWithFiles = {
          ...newDeponent,
          transcriptFiles: files.transcriptFiles || [],
          exhibitFiles: files.exhibitFiles || [],
          audioFiles: files.audioFiles || []
        };
        
        // Update the state before adding
        setNewDeponent(deponentWithFiles);
        
        // Now add the deponent and proceed
        setTimeout(() => {
          addDeponent();
          
          // Additional delay to ensure the deponent is added to the form state
          setTimeout(() => {
            const isValid = validateStep('deponent-info');
            if (isValid && onNext) {
              onNext();
            }
          }, 150);
        }, 100);
      });
    } else {
      const isValid = validateStep('deponent-info');
      if (isValid && onNext) {
        onNext();
      }
    }
  }, [validateStep, onNext, addDeponent, newDeponent]);

  const exhibitStatusOptions = [
    'No exhibits marked',
    'Electronic exhibit(s)',
    'Exhibit(s) emailed to production',
    'Exhibit(s) mailed to production',
    'Exhibit(s) uploaded to Agile Law',
    'Exhibit(s) retained by counsel',
    'Exhibit(s) handed in to Office'
  ];
  
  const carrierOptions = [
    'FedEx',
    'UPS',
    'USPS',
    'DHL',
    'Other'
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h2" component="h2" gutterBottom>
          Deponent Information
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          Review the pre-populated deponents and add any additional ones if needed.
        </Typography>

        {/* List of existing deponents as accordions */}
        {formData.deponents.length > 0 ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" gutterBottom>
              Deponents
            </Typography>
            
            {formData.deponents.map((deponent, index) => (
              <Accordion 
              key={deponent.id || index}
              expanded={expandedPanel === deponent.id}
              onChange={handleAccordionChange(deponent.id)}
              sx={{
                mb: 2,
                border: validationErrors[`deponent-${index}-name`] ? '1px solid rgba(231, 76, 60, 0.5)' : '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                '&:before': { display: 'none' }
              }}
            >
              <div style={{ position: 'relative' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`deponent-${deponent.id}-content`}
                  id={`deponent-${deponent.id}-header`}
                  tabIndex={0} // Make it focusable
                  sx={{ 
                    backgroundColor: validationErrors[`deponent-${index}-name`] ? 
                      'rgba(231, 76, 60, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    '&:focus': { 
                      outline: '2px solid #1976d2', 
                      outlineOffset: '2px' 
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 6 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {deponent.name || 'Unnamed Deponent'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        {deponent.exhibitStatus && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {deponent.exhibitStatus}
                          </Typography>
                        )}
                        {deponent.numPages > 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {deponent.numPages} pages
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {deponent.startTimeHour && deponent.startTimeMinute && deponent.startTimeAmPm && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {deponent.startTimeHour}:{deponent.startTimeMinute} {deponent.startTimeAmPm} - 
                          {deponent.endTimeHour && deponent.endTimeMinute && deponent.endTimeAmPm ? 
                            ` ${deponent.endTimeHour}:${deponent.endTimeMinute} ${deponent.endTimeAmPm}` : ' ?'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                
                
                <div 
                  style={{ 
                    position: 'absolute', 
                    right: '48px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    zIndex: 1 
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent accordion toggle
                  }}
                >
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation(); // Also stop propagation here
                      removeDeponent(index);
                    }}
                    aria-label="Remove deponent"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </div>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    {/* First row: Deponent Name and Exhibit Status side by side */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        id={`deponent-${deponent.id}-name-input`}
                        label="Deponent Name"
                        value={deponent.name}
                        onChange={(e) => handleChange(index, 'name', e.target.value)}
                        error={!!validationErrors[`deponent-${index}-name`]}
                        helperText={validationErrors[`deponent-${index}-name`]}
                        placeholder="Deponent Name"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id={`exhibit-status-label-${index}`}>Exhibit Status</InputLabel>
                        <Select
                          labelId={`exhibit-status-label-${index}`}
                          value={deponent.exhibitStatus || ''}
                          onChange={(e) => handleChange(index, 'exhibitStatus', e.target.value)}
                          label="Exhibit Status"
                        >
                          {exhibitStatusOptions.map(status => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Second Row: Transcript Information and potentially Exhibit Upload */}
                    <Grid item xs={12} md={
                      deponent.exhibitStatus === 'Electronic exhibit(s)' || 
                      deponent.exhibitStatus === 'Exhibit(s) mailed to production' ? 6 : 12
                    }>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, height: '100%' }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Transcript Information
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <FormControl component="fieldset">
                            <RadioGroup
                              row
                              value={deponent.transcriptType || 'standard'}
                              onChange={(e) => handleChange(index, 'transcriptType', e.target.value)}
                            >
                              <FormControlLabel 
                                value="standard" 
                                control={<Radio tabIndex={0} />} 
                                label="Transcript (ASCII/TXT)" 
                              />
                              <FormControlLabel 
                                value="realLegal" 
                                control={<Radio tabIndex={0} />} 
                                label="RealLegal Transcript" 
                              />
                              <FormControlLabel 
                                value="noTranscribe" 
                                control={<Radio tabIndex={0} />} 
                                label="Non-Transcribe" 
                              />
                            </RadioGroup>
                          </FormControl>
                        </Box>

                        {/* Transcript Upload Field - Show for standard type */}
                        {(deponent.transcriptType === 'standard' || !deponent.transcriptType) && (
                          <FileUploadField
                            title="Transcript Files"
                            type="transcriptFiles"
                            required={true}
                            deponentId={deponent.id}
                            maxFileSize={500}
                            error={validationErrors[`deponent-${index}-transcriptFiles`]}
                          />
                        )}

                        {/* Audio Upload Field - Show for noTranscribe type */}
                        {deponent.transcriptType === 'noTranscribe' && (
                          <FileUploadField
                            title="Audio Files or Rough Notes"
                            type="audioFiles"
                            required={true}
                            deponentId={deponent.id}
                            maxFileSize={500}
                            error={validationErrors[`deponent-${index}-audioFiles`]}
                          />
                        )}
                      </Paper>
                    </Grid>

                    {/* Exhibit Upload Section - only show if electronic exhibits */}
                    {deponent.exhibitStatus === 'Electronic exhibit(s)' && (
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, height: '100%' }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Exhibit Information
                          </Typography>
                          <FileUploadField
                            title="Exhibit Files"
                            type="exhibitFiles"
                            required={true}
                            deponentId={deponent.id}
                            maxFileSize={500}
                            error={validationErrors[`deponent-${index}-exhibitFiles`]}
                          />
                        </Paper>
                      </Grid>
                    )}
                    
                    {/* Shipping information - only show if exhibits are mailed to production */}
                    {deponent.exhibitStatus === 'Exhibit(s) mailed to production' && (
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, height: '100%' }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Exhibit Shipping Information
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel id={`carrier-label-${index}`}>Carrier</InputLabel>
                              <Select
                                labelId={`carrier-label-${index}`}
                                value={deponent.exhibitCarrier || ''}
                                onChange={(e) => handleChange(index, 'exhibitCarrier', e.target.value)}
                                label="Carrier"
                                required
                              >
                                {carrierOptions.map(carrier => (
                                  <MenuItem key={carrier} value={carrier}>{carrier}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              fullWidth
                              label="Tracking Number (optional)"
                              value={deponent.exhibitTrackingNumber || ''}
                              onChange={(e) => handleChange(index, 'exhibitTrackingNumber', e.target.value)}
                              placeholder="Enter tracking number"
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    
                    {/* Fourth Row: Page Count */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Number of Pages"
                        type="number"
                        value={deponent.numPages || ''}
                        onChange={(e) => handleChange(index, 'numPages', e.target.value)}
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        placeholder="Enter number of pages"
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    
                    {/* Fifth Row: Start Time */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ mr: 1, minWidth: '80px', mt: '-22px' }}>Start Time:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flex: 1, alignItems: 'flex-end' }}>
                          <TextField
                            label="Hour"
                            value={deponent.startTimeHour || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only allow numbers and ensure it's between 1-12 for AM/PM format
                              if ((value === '' || /^\d+$/.test(value)) && (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12))) {
                                handleChange(index, 'startTimeHour', value);
                              }
                            }}
                            onBlur={() => {
                              // Format with leading zero only when field loses focus
                              if (deponent.startTimeHour && deponent.startTimeHour.length === 1) {
                                handleChange(index, 'startTimeHour', `0${deponent.startTimeHour}`);
                              }
                            }}
                            placeholder="HH"
                            inputProps={{ maxLength: 2 }}
                            fullWidth
                            margin="normal"
                            sx={{ mt: 0 }}
                          />
                          <TextField
                            label="Minute"
                            value={deponent.startTimeMinute || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only allow numbers and ensure it's between 0-59
                              if ((value === '' || /^\d+$/.test(value)) && (value === '' || parseInt(value) <= 59)) {
                                handleChange(index, 'startTimeMinute', value);
                              }
                            }}
                            onBlur={() => {
                              // Format with leading zero only when field loses focus
                              if (deponent.startTimeMinute && deponent.startTimeMinute.length === 1) {
                                handleChange(index, 'startTimeMinute', `0${deponent.startTimeMinute}`);
                              }
                            }}
                            placeholder="MM"
                            inputProps={{ maxLength: 2 }}
                            fullWidth
                            margin="normal"
                            sx={{ mt: 0 }}
                          />
                          <FormControl sx={{ ml: 1 }}>
                            <RadioGroup
                              row
                              value={deponent.startTimeAmPm || ''}
                              onChange={(e) => handleChange(index, 'startTimeAmPm', e.target.value)}
                              sx={{
                                alignItems: 'center', // Align items in the center vertically
                                '& .MuiRadio-root': { 
                                  padding: '2px',    // Reduce padding around the actual radio button
                                },
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '0.875rem',  // Slightly smaller font size for the labels
                                  lineHeight: 1          // Tighter line height for the labels
                                }
                              }}
                            >
                              <FormControlLabel value="AM" control={<Radio size="small" tabIndex={0} />} label="AM" />
                              <FormControlLabel value="PM" control={<Radio size="small" tabIndex={0} />} label="PM" />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {/* Sixth Row: End Time */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ mr: 1, minWidth: '80px', mt: '-22px' }}>End Time:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flex: 1, alignItems: 'flex-end' }}>
                          <TextField
                            label="Hour"
                            value={deponent.endTimeHour || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only allow numbers and ensure it's between 1-12 for AM/PM format
                              if ((value === '' || /^\d+$/.test(value)) && (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12))) {
                                handleChange(index, 'endTimeHour', value);
                              }
                            }}
                            onBlur={() => {
                              // Format with leading zero only when field loses focus
                              if (deponent.endTimeHour && deponent.endTimeHour.length === 1) {
                                handleChange(index, 'endTimeHour', `0${deponent.endTimeHour}`);
                              }
                            }}
                            placeholder="HH"
                            inputProps={{ maxLength: 2 }}
                            fullWidth
                            margin="normal"
                            sx={{ mt: 0 }}
                          />
                          <TextField
                            label="Minute"
                            value={deponent.endTimeMinute || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only allow numbers and ensure it's between 0-59
                              if ((value === '' || /^\d+$/.test(value)) && (value === '' || parseInt(value) <= 59)) {
                                handleChange(index, 'endTimeMinute', value);
                              }
                            }}
                            onBlur={() => {
                              // Format with leading zero only when field loses focus
                              if (deponent.endTimeMinute && deponent.endTimeMinute.length === 1) {
                                handleChange(index, 'endTimeMinute', `0${deponent.endTimeMinute}`);
                              }
                            }}
                            placeholder="MM"
                            inputProps={{ maxLength: 2 }}
                            fullWidth
                            margin="normal"
                            sx={{ mt: 0 }}
                          />
                          <FormControl sx={{ ml: 1 }}>
                            <RadioGroup
                              row
                              value={deponent.endTimeAmPm || ''}
                              onChange={(e) => handleChange(index, 'endTimeAmPm', e.target.value)}
                              sx={{
                                alignItems: 'center', // Align items in the center vertically
                                '& .MuiRadio-root': { 
                                  padding: '2px',    // Reduce padding around the actual radio button
                                },
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '0.875rem',  // Slightly smaller font size for the labels
                                  lineHeight: 1          // Tighter line height for the labels
                                }
                              }}
                            >
                              <FormControlLabel value="AM" control={<Radio size="small" tabIndex={0} />} label="AM" />
                              <FormControlLabel value="PM" control={<Radio size="small" tabIndex={0} />} label="PM" />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {/* Additional Services Section */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Services:</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={deponent.techExpert || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, 'techExpert', e.target.checked)}
                                />
                              }
                              label="Tech/Expert"
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={deponent.video || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, 'video', e.target.checked)}
                                />
                              }
                              label="Video"
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={deponent.interpreter || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, 'interpreter', e.target.checked)}
                                />
                              }
                              label="Interpreter"
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={deponent.afterHours || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, 'afterHours', e.target.checked)}
                                />
                              }
                              label="After-hours"
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={deponent.weekendHoliday || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, 'weekendHoliday', e.target.checked)}
                                />
                              }
                              label="Weekend/Holiday"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Seventh Row: Read & Sign */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Read & Sign?</Typography>
                        <FormControl component="fieldset" error={!!validationErrors[`deponent-${index}-readAndSign`]}>
                          <RadioGroup
                            row
                            value={deponent.readAndSign === undefined ? '' : (deponent.readAndSign ? 'yes' : 'no')}
                            onChange={(e) => handleChange(index, 'readAndSign', e.target.value === 'yes')}
                          >
                            <FormControlLabel value="yes" control={<Radio tabIndex={0} />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio tabIndex={0} />} label="No" />
                          </RadioGroup>
                          {validationErrors[`deponent-${index}-readAndSign`] && (
                            <FormHelperText>{validationErrors[`deponent-${index}-readAndSign`]}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                      {deponent.readAndSign === true && (
                        <TextField
                          fullWidth
                          label="Send To"
                          value={deponent.sendErrataTo || ''}
                          onChange={(e) => handleChange(index, 'sendErrataTo', e.target.value)}
                          placeholder="Email address"
                        />
                      )}
                    </Grid>

                    {/* Eighth Row: Special Production Notes */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Special Production Notes (optional)"
                        value={deponent.specialProductionNotes || ''}
                        onChange={(e) => handleChange(index, 'specialProductionNotes', e.target.value)}
                        placeholder="Any special instructions or notes for transcript production"
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            No deponents added yet. Use the form below to add a deponent.
            {validationErrors.deponents && (
              <FormHelperText error>{validationErrors.deponents}</FormHelperText>
            )}
          </Alert>
        )}

        {/* Form to add new deponent */}
        <Box sx={{ mb: 4 }}>
          <Accordion 
            sx={{
              mb: 2,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              '&:before': { display: 'none' },
            }}
            expanded={expandedPanel === 'new-deponent'}
            onChange={handleAccordionChange('new-deponent')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="add-deponent-content"
              id="add-deponent-header"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                Add New Deponent
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* First row: Deponent Name and Exhibit Status side by side */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="deponent-name"
                    name="name"
                    label="Deponent Name"
                    value={newDeponent.name}
                    onChange={handleNewDeponentChange}
                    placeholder="Enter deponent name"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="exhibit-status-label">Exhibit Status</InputLabel>
                    <Select
                      labelId="exhibit-status-label"
                      id="exhibit-status"
                      name="exhibitStatus"
                      value={newDeponent.exhibitStatus || ''}
                      onChange={handleNewDeponentSelectChange}
                      label="Exhibit Status"
                    >
                      {exhibitStatusOptions.map(status => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Second Row: Transcript Information and potentially Exhibit Upload */}
                <Grid item xs={12} md={
                  newDeponent.exhibitStatus === 'Electronic exhibit(s)' || 
                  newDeponent.exhibitStatus === 'Exhibit(s) mailed to production' ? 6 : 12
                }>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Transcript Information
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          name="transcriptType"
                          value={newDeponent.transcriptType || 'standard'}
                          onChange={(e) => {
                            setNewDeponent(prev => ({
                              ...prev,
                              transcriptType: e.target.value as 'standard' | 'realLegal' | 'noTranscribe'
                            }));
                          }}
                        >
                          <FormControlLabel 
                            value="standard" 
                            control={<Radio tabIndex={0} />} 
                            label="Transcript (ASCII/TXT)" 
                          />
                          <FormControlLabel 
                            value="realLegal" 
                            control={<Radio tabIndex={0} />} 
                            label="RealLegal Transcript" 
                          />
                          <FormControlLabel 
                            value="noTranscribe" 
                            control={<Radio tabIndex={0} />} 
                            label="Non-Transcribe" 
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    {/* Transcript Upload Field - Show for standard type */}
                    {(newDeponent.transcriptType === 'standard' || !newDeponent.transcriptType) && (
                      <FileUploadField
                        title="Transcript Files"
                        type="transcriptFiles"
                        required={true}
                        deponentId={newDeponent.id}
                        maxFileSize={500}
                        key={`transcript-field-${newDeponent.id}`}
                      />
                    )}

                    {/* Audio Upload Field - Show for noTranscribe type */}
                    {newDeponent.transcriptType === 'noTranscribe' && (
                      <FileUploadField
                        title="Audio Files or Rough Notes"
                        type="audioFiles"
                        required={true}
                        deponentId={newDeponent.id}
                        maxFileSize={500}
                        key={`audio-field-${newDeponent.id}`}
                      />
                    )}
                  </Paper>
                </Grid>

                {/* Exhibit Upload Section - only show if electronic exhibits */}
                {newDeponent.exhibitStatus === 'Electronic exhibit(s)' && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Exhibit Information
                      </Typography>
                      <FileUploadField
                        title="Exhibit Files"
                        type="exhibitFiles"
                        required={true}
                        deponentId={newDeponent.id}
                        maxFileSize={500}
                        key={`exhibit-field-${newDeponent.id}`}
                      />
                    </Paper>
                  </Grid>
                )}
                
                {/* Shipping information - only show if exhibits are mailed to production */}
                {newDeponent.exhibitStatus === 'Exhibit(s) mailed to production' && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Exhibit Shipping Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel id="new-carrier-label">Carrier</InputLabel>
                          <Select
                            labelId="new-carrier-label"
                            name="exhibitCarrier"
                            value={newDeponent.exhibitCarrier || ''}
                            onChange={handleNewDeponentSelectChange}
                            label="Carrier"
                            required
                          >
                            {carrierOptions.map(carrier => (
                              <MenuItem key={carrier} value={carrier}>{carrier}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          id="tracking-number"
                          name="exhibitTrackingNumber"
                          label="Tracking Number (optional)"
                          value={newDeponent.exhibitTrackingNumber || ''}
                          onChange={handleNewDeponentChange}
                          placeholder="Enter tracking number"
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}
                
                {/* Fourth Row: Page Count */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    id="num-pages"
                    name="numPages"
                    label="Number of Pages"
                    type="number"
                    value={newDeponent.numPages || ''}
                    onChange={handleNewDeponentChange}
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    placeholder="Enter number of pages"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                
                {/* Fifth Row: Start Time */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>Start Time:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flex: 1, alignItems: 'flex-end' }}>
                      <TextField
                        label="Hour"
                        name="startTimeHour"
                        value={newDeponent.startTimeHour}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and ensure it's between 1-12 for AM/PM format
                          if ((value === '' || /^\d+$/.test(value)) && (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12))) {
                            setNewDeponent(prev => ({
                              ...prev,
                              startTimeHour: value
                            }));
                          }
                        }}
                        onBlur={() => {
                          // Format with leading zero only when field loses focus
                          if (newDeponent.startTimeHour && newDeponent.startTimeHour.length === 1) {
                            setNewDeponent(prev => ({
                              ...prev,
                              startTimeHour: `0${newDeponent.startTimeHour}`
                            }));
                          }
                        }}
                        placeholder="HH"
                        inputProps={{ maxLength: 2 }}
                        fullWidth
                        margin="normal"
                        sx={{ mt: 0 }}
                      />
                      <TextField
                        label="Minute"
                        name="startTimeMinute"
                        value={newDeponent.startTimeMinute}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and ensure it's between 0-59
                          if ((value === '' || /^\d+$/.test(value)) && (value === '' || parseInt(value) <= 59)) {
                            setNewDeponent(prev => ({
                              ...prev,
                              startTimeMinute: value
                            }));
                          }
                        }}
                        onBlur={() => {
                          // Format with leading zero only when field loses focus
                          if (newDeponent.startTimeMinute && newDeponent.startTimeMinute.length === 1) {
                            setNewDeponent(prev => ({
                              ...prev,
                              startTimeMinute: `0${newDeponent.startTimeMinute}`
                            }));
                          }
                        }}
                        placeholder="MM"
                        inputProps={{ maxLength: 2 }}
                        fullWidth
                        margin="normal"
                        sx={{ mt: 0 }}
                      />
                      <FormControl sx={{ ml: 1 }}>
                        <RadioGroup
                          row
                          value={newDeponent.startTimeAmPm}
                          name="startTimeAmPm"
                          onChange={(e) => {
                            setNewDeponent(prev => ({
                              ...prev,
                              startTimeAmPm: e.target.value as 'AM' | 'PM' | ''
                            }));
                          }}
                          sx={{
                            alignItems: 'center', // Align items in the center vertically
                            '& .MuiRadio-root': { 
                              padding: '2px',    // Reduce padding around the actual radio button
                            },
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.875rem',  // Slightly smaller font size for the labels
                              lineHeight: 1          // Tighter line height for the labels
                            }
                          }}
                        >
                          <FormControlLabel value="AM" control={<Radio size="small" tabIndex={0} />} label="AM" />
                          <FormControlLabel value="PM" control={<Radio size="small" tabIndex={0} />} label="PM" />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Box>
                </Grid>
                
                {/* Sixth Row: End Time */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>End Time:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flex: 1, alignItems: 'flex-end' }}>
                      <TextField
                        label="Hour"
                        name="endTimeHour"
                        value={newDeponent.endTimeHour}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and ensure it's between 1-12 for AM/PM format
                          if ((value === '' || /^\d+$/.test(value)) && (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12))) {
                            setNewDeponent(prev => ({
                              ...prev,
                              endTimeHour: value
                            }));
                          }
                        }}
                        onBlur={() => {
                          // Format with leading zero only when field loses focus
                          if (newDeponent.endTimeHour && newDeponent.endTimeHour.length === 1) {
                            setNewDeponent(prev => ({
                              ...prev,
                              endTimeHour: `0${newDeponent.endTimeHour}`
                            }));
                          }
                        }}
                        placeholder="HH"
                        inputProps={{ maxLength: 2 }}
                        fullWidth
                        margin="normal"
                        sx={{ mt: 0 }}
                      />
                      <TextField
                        label="Minute"
                        name="endTimeMinute"
                        value={newDeponent.endTimeMinute}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and ensure it's between 0-59
                          if ((value === '' || /^\d+$/.test(value)) && (value === '' || parseInt(value) <= 59)) {
                            setNewDeponent(prev => ({
                              ...prev,
                              endTimeMinute: value
                            }));
                          }
                        }}
                        onBlur={() => {
                          // Format with leading zero only when field loses focus
                          if (newDeponent.endTimeMinute && newDeponent.endTimeMinute.length === 1) {
                            setNewDeponent(prev => ({
                              ...prev,
                              endTimeMinute: `0${newDeponent.endTimeMinute}`
                            }));
                          }
                        }}
                        placeholder="MM"
                        inputProps={{ maxLength: 2 }}
                        fullWidth
                        margin="normal"
                        sx={{ mt: 0 }}
                      />
                      <FormControl sx={{ ml: 1 }}>
                        <RadioGroup
                          row
                          value={newDeponent.endTimeAmPm}
                          name="endTimeAmPm"
                          onChange={(e) => {
                            setNewDeponent(prev => ({
                              ...prev,
                              endTimeAmPm: e.target.value as 'AM' | 'PM' | ''
                            }));
                          }}
                          sx={{
                            alignItems: 'center', // Align items in the center vertically
                            '& .MuiRadio-root': { 
                              padding: '2px',    // Reduce padding around the actual radio button
                            },
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.875rem',  // Slightly smaller font size for the labels
                              lineHeight: 1          // Tighter line height for the labels
                            }
                          }}
                        >
                          <FormControlLabel value="AM" control={<Radio size="small" tabIndex={0} />} label="AM" />
                          <FormControlLabel value="PM" control={<Radio size="small" tabIndex={0} />} label="PM" />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Box>
                </Grid>
                
                {/* Additional Services Section */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Services:</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={newDeponent.techExpert || false}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setNewDeponent(prev => ({
                                  ...prev,
                                  techExpert: e.target.checked
                                }));
                              }}
                            />
                          }
                          label="Tech/Expert"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={newDeponent.video || false}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setNewDeponent(prev => ({
                                  ...prev,
                                  video: e.target.checked
                                }));
                              }}
                            />
                          }
                          label="Video"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={newDeponent.interpreter || false}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setNewDeponent(prev => ({
                                  ...prev,
                                  interpreter: e.target.checked
                                }));
                              }}
                            />
                          }
                          label="Interpreter"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={newDeponent.afterHours || false}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setNewDeponent(prev => ({
                                  ...prev,
                                  afterHours: e.target.checked
                                }));
                              }}
                            />
                          }
                          label="After-hours"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={newDeponent.weekendHoliday || false}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setNewDeponent(prev => ({
                                  ...prev,
                                  weekendHoliday: e.target.checked
                                }));
                              }}
                            />
                          }
                          label="Weekend/Holiday"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                
                {/* Seventh Row: Read & Sign */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Read & Sign?</Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={newDeponent.readAndSign === undefined ? '' : (newDeponent.readAndSign ? 'yes' : 'no')}
                        onChange={(e) => {
                          setNewDeponent(prev => ({
                            ...prev,
                            readAndSign: e.target.value === 'yes',
                            sendErrataTo: e.target.value === 'no' ? '' : prev.sendErrataTo
                          }));
                        }}
                      >
                        <FormControlLabel value="yes" control={<Radio tabIndex={0} />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio tabIndex={0} />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  {newDeponent.readAndSign === true && (
                    <TextField
                      fullWidth
                      id="send-errata-to"
                      name="sendErrataTo"
                      label="Send To"
                      value={newDeponent.sendErrataTo || ''}
                      onChange={handleNewDeponentChange}
                      placeholder="Email address"
                    />
                  )}
                </Grid>

                {/* Eighth Row: Special Production Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="special-production-notes"
                    name="specialProductionNotes"
                    label="Special Production Notes (optional)"
                    value={newDeponent.specialProductionNotes || ''}
                    onChange={handleNewDeponentChange}
                    placeholder="Any special instructions or notes for transcript production"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained" 
                  color="error"
                  onClick={() => {
                    setNewDeponent({
                      name: '',
                      exhibitStatus: '',
                      exhibitCarrier: '',
                      exhibitTrackingNumber: '',
                      readAndSign: undefined,
    sendErrataTo: '',
                      specialProductionNotes: '',
                      id: tempId.current,
                      numPages: 0,
                      startTimeHour: '',
                      startTimeMinute: '',
                      startTimeAmPm: '',
                      endTimeHour: '',
                      endTimeMinute: '',
                      endTimeAmPm: '',
                      transcriptType: 'standard',
                      transcriptFiles: [],
                      exhibitFiles: [],
                      audioFiles: [],
                      techExpert: false,
                      video: false,
                      interpreter: false,
                      afterHours: false,
                      weekendHoliday: false
                    });
                  }}
                  sx={{ mt: 2, mr: 2 }}
                >
                  Clear Form
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={addDeponent}
                  disabled={!newDeponent.name.trim()}
                  sx={{ mt: 2 }}
                >
                  Add Deponent
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
          >
            Continue to Attorney Information
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DeponentInfo;